"use client";

import { lazyComponent } from "@/components/ui/lazy-component";
import { LoadFailureNotice } from "@/components/ui/load-failure-notice";
import type { ComponentProps } from "react";
import { cn } from "@/lib/utils";
import { TABLET_BREAKPOINT, useMediaMatches } from "@/hooks/use-mobile";

type Props = ComponentProps<
  (typeof import("./listings-cluster-map"))["ListingsClusterMap"]
>;

function MapSkeleton() {
  return (
    <div className="flex h-full w-full items-center justify-center rounded-lg border border-border bg-muted/40 text-sm text-muted-foreground">
      Loading map…
    </div>
  );
}

const ListingsClusterMapInner = lazyComponent(
  () => import("./listings-cluster-map").then((mod) => mod.ListingsClusterMap),
  {
    ssr: false,
    loading: <MapSkeleton />,
    failure: {
      label: "listings-cluster-map",
      fallback: (
        <LoadFailureNotice title="The map could not load." className="h-full" />
      ),
    },
  },
);

function viewportMedia(viewport: "desktop" | "mobile" | undefined) {
  if (viewport === "desktop") return `(min-width: ${TABLET_BREAKPOINT}px)`;
  if (viewport === "mobile") return `(max-width: ${TABLET_BREAKPOINT - 1}px)`;
  return undefined;
}

export function ListingsClusterMapClient(
  props: Props & {
    viewport?: "desktop" | "mobile";
    mapClassName?: string;
  },
) {
  const {
    className,
    viewport,
    mapClassName = "h-full w-full",
    ...rest
  } = props;
  const isViewportActive = useMediaMatches(viewportMedia(viewport));

  return (
    <div className={cn("relative", className)}>
      {isViewportActive ? (
        <ListingsClusterMapInner {...rest} className={mapClassName} />
      ) : null}
    </div>
  );
}
